import { RingCentralQueryService } from '@supply-mover-2-0/core';
import type {
	RingCentralDialOptionsModel,
	RingCentralDialResponseDto,
	RingCentralUserDialStatusDto,
} from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

interface RingCentralContextValue {
	dialStatus: RingCentralUserDialStatusDto | null;
	isLoading: boolean;
	isDialing: boolean;
	error: string | null;
	canDial: boolean;
	dial: (
		phoneNumber: string,
		options?: RingCentralDialOptionsModel,
	) => Promise<RingCentralDialResponseDto>;
	refreshStatus: () => Promise<void>;
}

const RingCentralContext = createContext<RingCentralContextValue | undefined>(
	undefined,
);

export interface RingCentralProviderProps {
	children: React.ReactNode;
}

export const RingCentralProvider: React.FC<RingCentralProviderProps> = ({
	children,
}) => {
	const [dialStatus, setDialStatus] =
		useState<RingCentralUserDialStatusDto | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isDialing, setIsDialing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refreshStatus = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const status = await RingCentralQueryService.getUserDialStatus(true);
			setDialStatus(status);
		} catch (err) {
			console.error('Failed to load RingCentral status:', err);
			setError('Failed to load RingCentral status');
			setDialStatus({
				connected: false,
				canDial: false,
				needsUserAuth: false,
				userAuthenticated: false,
				phoneExtensionId: null,
				phoneExtensionNumber: null,
				callMode: 'ringout',
				userPhoneNumber: null,
			});
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void refreshStatus();
	}, [refreshStatus]);

	const dial = useCallback(
		async (
			phoneNumber: string,
			options?: RingCentralDialOptionsModel,
		): Promise<RingCentralDialResponseDto> => {
			if (dialStatus?.canDial !== true) {
				return {
					success: false,
					error: 'User is not configured for click-to-dial',
				};
			}

			try {
				setIsDialing(true);

				return await RingCentralQueryService.dial(phoneNumber, options);
			} catch (err) {
				console.error('Failed to dial:', err);

				return {
					success: false,
					error: 'Failed to initiate call. Please try again.',
				};
			} finally {
				setIsDialing(false);
			}
		},
		[dialStatus?.canDial],
	);

	const canDial = dialStatus?.canDial ?? false;

	return (
		<RingCentralContext.Provider
			value={{
				dialStatus,
				isLoading,
				isDialing,
				error,
				canDial,
				dial,
				refreshStatus,
			}}
		>
			{children}
		</RingCentralContext.Provider>
	);
};

/**
 * Hook to access RingCentral context
 * Returns null if not in a RingCentralProvider
 */
export const useRingCentralSafe = (): RingCentralContextValue | null =>
	useContext(RingCentralContext) ?? null;
